/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { css, SerializedStyles } from '@emotion/react';
import { clearCopyToClipboardAction } from './copyToClipboardAction';
import { showSnackbarAction } from '../snackbar/snackbarAction';
import { IAppState } from '../core/redux/IAppState';
import { SHORT } from '../snackbar/snackbarDuration';


/**
 * CopyToClipboardHost: a component that connects to the Redux store and copies text from a textbox offscreen to
 * the system's clipboard and then shows a Snackbar to confirm.
 *
 * This sits in index.tsx with other non-rendering components as it does not render anything on-screen.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  // Map state to props
  (state: IAppState, props) => R.mergeDeepRight(
    props,
    {
      textToCopy: state.textToClipboard,
      successMsg: state.clipboardMsg,
    },
  ),
  // Map dispatch to props
  (dispatch, props) => R.mergeDeepRight(props, {
    clearClipboardState: () => dispatch(clearCopyToClipboardAction),
    showSnackbar:           (message: string, isError: boolean, timeoutMs: number, priority?: number) => dispatch(
      showSnackbarAction(message, isError, timeoutMs, priority),
    ),
  }),
)(class CopyToClipboardHost extends React.PureComponent <{
  textToCopy?: string;
  successMsg?: string;
  clearClipboardState?: () => void;
  showSnackbar?: (message: string, isError: boolean, timeoutMs: number, priority?: number) => void;
  rootCss?: SerializedStyles;
}> {
  public static defaultProps = {
    textToCopy: '',
    rootClass:  undefined,
  };

  private readonly inputRef: React.RefObject<HTMLInputElement | null>;


  constructor(props) {
    super(props);

    this.inputRef = React.createRef();
  }


  public componentDidUpdate(prevProps) {
    // If we have new text to copy, copy it
    if (this.props.textToCopy && prevProps.textToCopy !== this.props.textToCopy) {
      if (!R.path([ 'inputRef', 'current' ], this)) {
        return;
      }

      this.inputRef.current!.select();
      document.execCommand('copy');

      if (this.props.successMsg) {
        this.props.showSnackbar!(
          this.props.successMsg,
          false,
          SHORT,
          10, // High priority for user feedback
        );
      }

      this.props.clearClipboardState!();
    }
  }


  public render() {
    return (
      <div css={this.props.rootCss}>
        {
          <input id="copyToClipboardInput"
                 type="text"
                 css={css`position: absolute; left: -1000px; top: -1000px;`}
                 tabIndex={-1}
                 ref={this.inputRef}
                 value={this.props.textToCopy}
                 readOnly={true}
          />
        }
      </div>
    );
  }
});
